import React, { useState, useRef } from 'react';
import { Paper, TextField, Button, Typography, Box, Link as MuiLink, Container, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePageTranslation } from '../i18n/hooks';
import axiosInstance from '../utils/axios';
import SEO from '../components/SEO';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// ─── Mascot states ───────────────────────────────────────────────────────────
// The Qualitati dog watches the username field, covers eyes for password,
// peeks when you toggle visibility, and reacts to errors/success.
const MASCOT_STATES = {
  idle: { img: '/logon/mimi1.png', label: 'Hello!' },
  watching: { img: '/logon/mimi2.png', label: 'I see you typing...' },
  hiding: { img: '/logon/mimi3.png', label: 'I won\'t peek!' },
  peeking: { img: '/logon/mimi2.png', label: 'Okay, just a peek...' },
  happy: { img: '/logon/mimi4.png', label: 'Welcome back!' },
  sad: { img: '/logon/mimi1.png', label: 'Oops, try again!' },
  thinking: { img: '/logon/mimi1.png', label: 'Logging in...' },
};

const Login = () => {
  const { t } = usePageTranslation('auth');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [inlineError, setInlineError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mascotState, setMascotState] = useState('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const passwordRef = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setInlineError('');
    setMascotState('thinking');
    setIsSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await axiosInstance.post('/api/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (process.env.NODE_ENV === 'development') console.log('Login response:', response.data);

      // Two-factor enabled: server emailed a code, switch to the verify step.
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setMfaCode('');
        setMascotState('idle');
        return;
      }

      if (response.data.access_token) {
        await finishLogin(response.data);
      }
    } catch (error) {
      console.error('Login error:', error.response?.data || error);
      const detail = error.response?.data?.detail || '';
      let msg;
      if (detail.toLowerCase().includes('incorrect') || detail.toLowerCase().includes('password')) {
        msg = t('messages.incorrectCredentials');
      } else if (detail.toLowerCase().includes('verify')) {
        msg = t('messages.emailNotVerified');
      } else {
        msg = t('messages.loginError');
      }
      setMascotState('sad');
      setInlineError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
      // Shake back to idle after a moment
      setTimeout(() => setMascotState('idle'), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shared post-authentication flow (used by both the normal and MFA paths).
  const finishLogin = async (data) => {
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    localStorage.setItem('username', credentials.username);
    setMascotState('happy');
    try {
      await axiosInstance.get('/api/user/profile');
      // Resume an in-progress flow via ?redirect= — internal paths only.
      const redirectTo = new URLSearchParams(location.search).get('redirect');
      if (redirectTo && redirectTo.startsWith('/')) {
        navigate(redirectTo, { replace: true });
        return;
      }
      setTimeout(() => navigate('/projects'), 600);
    } catch (error) {
      console.error('Token verification failed:', error);
      setMascotState('sad');
      setSnackbar({ open: true, message: 'Login successful but token verification failed. Please try again.', severity: 'error' });
    }
  };

  const handleVerifyMfa = async (event) => {
    event.preventDefault();
    setInlineError('');
    setMascotState('thinking');
    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post('/api/login/verify-mfa', {
        username: credentials.username,
        code: mfaCode.trim(),
      });
      if (response.data.access_token) {
        await finishLogin(response.data);
      }
    } catch (error) {
      const msg = t('messages.mfaInvalid') || 'Invalid or expired code. Please try again.';
      setMascotState('sad');
      setInlineError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setTimeout(() => setMascotState('idle'), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentMascot = MASCOT_STATES[mascotState] || MASCOT_STATES.idle;

  return (
    <>
      <SEO
        title="Login"
        description="Login to Qualitati to access your AI-powered interview projects and manage your research."
        canonical="https://qualitati.com/login"
        noindex={true}
      />
      <Container maxWidth="xs" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {/* ─── Mascot ─── */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: -8,
            mb: 2,
          }}>
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                bgcolor: '#F0FDFA',
                border: '3px solid #CCFBF1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: mascotState === 'happy' ? 'scale(1.15) rotate(5deg)' :
                  mascotState === 'sad' ? 'scale(0.9)' :
                    mascotState === 'thinking' ? 'scale(1.05)' :
                      mascotState === 'hiding' ? 'translateY(-4px)' : 'scale(1)',
                boxShadow: mascotState === 'happy' ? '0 4px 20px rgba(13, 148, 136, 0.3)' :
                  '0 2px 8px rgba(0, 0, 0, 0.08)',
                filter: mascotState === 'sad' ? 'grayscale(0.5)' : 'none',
                animation: mascotState === 'sad' ? 'shake 0.5s ease-in-out' :
                  mascotState === 'thinking' ? 'pulse 1.2s ease-in-out infinite' : 'none',
                '@keyframes shake': {
                  '0%, 100%': { transform: 'translateX(0) scale(0.9)' },
                  '25%': { transform: 'translateX(-6px) scale(0.9)' },
                  '75%': { transform: 'translateX(6px) scale(0.9)' },
                },
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1.05)' },
                  '50%': { transform: 'scale(1.1)' },
                },
              }}
            >
              <img
                src={currentMascot.img}
                alt={currentMascot.label}
                style={{ width: '85%', height: '85%', objectFit: 'cover' }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: mascotState === 'happy' ? '#059669' :
                  mascotState === 'sad' ? '#DC2626' : '#6B7280',
                fontWeight: 500,
                fontSize: '0.8rem',
                transition: 'all 0.3s ease',
                minHeight: 20,
              }}
            >
              {currentMascot.label}
            </Typography>
          </Box>

          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              mb: 3,
              color: '#111827',
            }}
          >
            {t('actions.login')}
          </Typography>

          <Box component="form" onSubmit={mfaRequired ? handleVerifyMfa : handleSubmit}>
            {!mfaRequired && (
              <>
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('fields.username') + ' / Email'}
                  placeholder="Username or Email"
                  value={credentials.username}
                  onFocus={() => setMascotState('watching')}
                  onBlur={() => {
                    if (!passwordRef.current?.contains(document.activeElement)) {
                      setMascotState('idle');
                    }
                  }}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('fields.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  ref={passwordRef}
                  onFocus={() => setMascotState(showPassword ? 'peeking' : 'hiding')}
                  onBlur={() => setMascotState('idle')}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setShowPassword(!showPassword);
                            setMascotState(!showPassword ? 'peeking' : 'hiding');
                          }}
                          edge="end"
                          size="small"
                          sx={{ color: '#9CA3AF' }}
                        >
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            )}

            {mfaRequired && (
              <>
                <Typography sx={{ mt: 1, mb: 1, color: '#374151', fontSize: '0.9rem' }}>
                  {t('messages.mfaPrompt') || 'We emailed you a 6-digit code. Enter it below to finish signing in.'}
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  autoFocus
                  label={t('messages.mfaCodeLabel') || 'Verification code'}
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', fontSize: '1.2rem', textAlign: 'center' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            )}

            {inlineError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setInlineError('')}>
                {inlineError}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: '#0D9488',
                '&:hover': { bgcolor: '#0F766E' },
              }}
            >
              {isSubmitting
                ? (mfaRequired ? (t('messages.mfaVerifying') || 'Verifying...') : (t('messages.loggingIn') || 'Logging in...'))
                : (mfaRequired ? (t('messages.mfaVerify') || 'Verify code') : t('actions.login'))}
            </Button>

            {mfaRequired && (
              <Button
                fullWidth
                variant="text"
                onClick={() => { setMfaRequired(false); setMfaCode(''); setInlineError(''); }}
                sx={{ mb: 2, color: '#6B7280', textTransform: 'none' }}
              >
                {t('messages.mfaBack') || 'Back to login'}
              </Button>
            )}

            {!mfaRequired && (
            <Button
              component={Link}
              to="/register"
              fullWidth
              variant="outlined"
              sx={{
                mb: 2,
                py: 1.2,
                borderRadius: 2,
                borderColor: '#E5E7EB',
                color: '#374151',
                '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
              }}
            >
              {t('actions.register') || 'Register'}
            </Button>
            )}

            {!mfaRequired && (
            <>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <MuiLink
                component={Link}
                to="/forgot-password"
                underline="hover"
                sx={{ color: '#6B7280', fontSize: '0.85rem' }}
              >
                {t('actions.forgotPassword')}
              </MuiLink>
              <MuiLink
                component={Link}
                to="/forgot-username"
                underline="hover"
                sx={{ color: '#6B7280', fontSize: '0.85rem' }}
              >
                {t('actions.forgotUsername')}
              </MuiLink>
            </Box>
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <MuiLink
                component={Link}
                to="/resend-verification"
                underline="hover"
                sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}
              >
                {t('actions.resendVerification')}
              </MuiLink>
            </Box>
            </>
            )}
          </Box>
        </Paper>
      </Container>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
